import React, { useContext, useState } from 'react';
import CustomButton from '../UI/CustomButton';
import { SVGs } from '../../constants/Svgs';
import { DifficultySummaryItemProps, GymMinimalProps } from '../../constants/types';
import { pages } from '../../constants/Consts';
import { GlobalContext } from '../../context/GlobalStateProvider';
import Modal from '../UI/ModalRoute';
import { useNavigate } from 'react-router-dom';
import { TailSpin } from 'react-loader-spinner';

interface NavBarBackOfficeProps {
    profilePicture?: string;
    firstName?: string;
    lastName?: string;
    gyms?: string;
    difficultySummary?: DifficultySummaryItemProps[];
    totalRoutesCount?: number;
    difficultySystem?: boolean;
}

interface NavItemProps {
    icon: React.FC;
    label: string;
    isActive: boolean;
    onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon: Icon, label, isActive, onClick }) => {
    return (
        <div
            onClick={isActive ? undefined : onClick}
            className={`
                flex items-center gap-2 px-2 py-1.5 rounded-md w-full
                transition-all duration-200
                ${isActive
                    ? 'bg-backoffice-300 text-backoffice-800 cursor-default'
                    : 'text-grey-700 hover:bg-grey-100 hover:text-backoffice-700 active:scale-[0.97] cursor-pointer'
                }
            `}
        >
            <Icon />
            <span className='regular normal'>{label}</span>
        </div>
    );
};

const NavBarBackOffice: React.FC<NavBarBackOfficeProps> = ({ profilePicture, firstName, lastName, gyms }) => {
    const globalContext = useContext(GlobalContext);
    const { currentPage, setCurrentGym, gymsFilterInfo, currentGym, routeSetterInfo, setUser } = globalContext;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();

    const handleGymSelect = (gym: GymMinimalProps) => {
        setCurrentGym(gym);
        localStorage.setItem('favoriteGymInfo', JSON.stringify(gym));
        setIsModalOpen(false);
        window.location.reload();
    };

    return (
        <nav>
            <div className="h-full flex w-52 px-6 pt-8 flex-col items-start justify-start flex-shrink-0 text-grey-600 gap-12">
                <div className="self-center">
                    <CustomButton
                        Svg={SVGs.gastonOuvertureSmall}
                        onClickProp={() => navigate('/homepagebo')}
                    />
                </div>
                <div className="flex flex-col items-start max-w-full">
                    <div className="flex flex-col items-start gap-8 self-stretch">
                        <div className="flex flex-col self-stretch items-start gap-2.5">
                            {gyms &&
                                <div className='bg-backoffice-500 rounded-[10px] py-2 px-3 text-white bold normal w-full cursor-default'>
                                    {gyms}
                                </div>
                            }
                            <div
                                onClick={() => setIsModalOpen(true)}
                                className='flex items-center gap-3 bg-backoffice-800 rounded-[10px] py-2 px-3 text-grey-100 bold normal w-full cursor-pointer transition-all duration-200 hover:bg-backoffice-700 active:scale-[0.97]'
                            >
                                <SVGs.changeGym />
                                Changer salle
                            </div>
                        </div>

                        <div className="border-b border-grey-700 w-[104px] opacity-50"></div>

                        <div className="flex flex-col items-start gap-3 self-stretch">
                            <NavItem
                                icon={SVGs.smallHouse}
                                label="Accueil"
                                isActive={currentPage === pages.accueil}
                                onClick={() => navigate('/homepagebo')}
                            />
                            <NavItem
                                icon={SVGs.sectorsIcon}
                                label="Secteurs"
                                isActive={currentPage === pages.secteurs}
                                onClick={() => navigate('/sectors-details')}
                            />
                            <NavItem
                                icon={SVGs.circuitsIcon}
                                label="Circuits"
                                isActive={currentPage === pages.circuits}
                                onClick={() => navigate('/circuits-overview')}
                            />
                            <NavItem
                                icon={SVGs.commentsIcon}
                                label="Commentaires"
                                isActive={currentPage === pages.commentaires}
                                onClick={() => navigate('/comments')}
                            />
                            <NavItem
                                icon={SVGs.boulderDetailsIcon}
                                label="Détail blocs"
                                isActive={currentPage === pages.detailBlocs}
                                onClick={() => navigate('/bomain')}
                            />
                        </div>

                        <div className="border-b border-grey-700 w-[104px] opacity-50"></div>

                        <div className="flex flex-col items-start gap-4">
                            <div className="flex items-center gap-3">
                                <div className="flex justify-center">
                                    <img
                                        src={profilePicture}
                                        alt="profile-picture"
                                        className="w-9 h-9 rounded-full object-cover"
                                    />
                                </div>
                                <div className="regular normal">
                                    {firstName} {lastName}
                                </div>
                            </div>
                            <div className="flex flex-col items-start gap-2 self-stretch">
                                {routeSetterInfo && routeSetterInfo.isAdmin &&
                                    <NavItem
                                        icon={SVGs.settingsIcon}
                                        label="Paramètres"
                                        isActive={currentPage === pages.parameters}
                                        onClick={() => navigate('/parameters-admin')}
                                    />
                                }
                                <div
                                    onClick={() => {
                                        localStorage.removeItem('user');
                                        navigate('/login-pro');
                                        setUser('');
                                    }}
                                    className='flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-grey-700 transition-all duration-200 hover:bg-red-25 hover:text-red-600 active:scale-[0.97] w-full'
                                >
                                    <SVGs.logoutIcon />
                                    <span className='regular normal'>Déconnexion</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} titleModal="Choix salle" width='fit-content' position='relative'>
                    <div className="gym-list">
                        {gymsFilterInfo ?
                            gymsFilterInfo.map((gym) => (
                                <CustomButton
                                    key={gym.id}
                                    customStyle={currentGym && gym.id === currentGym.id ? "select-gym-button" : "select-gym-button-inactive"}
                                    onClickProp={() => handleGymSelect(gym)}
                                    text={gym.name}
                                    customTextStyle='bold large2'
                                />
                            ))
                            :
                            <div style={{ alignSelf: 'center' }}>
                                <TailSpin color="#0099CC" height={80} width={80} />
                            </div>
                        }
                    </div>
                </Modal>
            </div>
        </nav>
    );
}

export default NavBarBackOffice;
